import React from 'react';
import PropTypes from 'prop-types';
import { DataRow, Divider, Expanded, Row } from '@tempmee/react';
import { ReviewCard } from './ReviewCard';
import { orNotAvailable } from './utils';

/**
 * The office's review of the professional, for one shift.
 *
 * **No loading or error state, deliberately.** Its data is already in
 * `state.jobs.rating` before this ever mounts — `JobDetails` holds the entire
 * tab area behind a page-level spinner until `admin/jobs/{id}` resolves — so a
 * spinner here would be unreachable code and an error state would be a lie.
 * That asymmetry with the office card beside it is the point, not an oversight:
 * it is what makes "the other card still renders if its data resolved" true
 * structurally rather than by careful wiring. Making the two cards symmetrical
 * is the tempting refactor that would reintroduce a state that cannot happen.
 *
 * `Confidential Feedback for GoTu` keeps its name. It is the office→professional
 * field, and the "Private Feedback" on the office card is a different one — the
 * professional's confidential note *about the office*. Renaming this to match
 * would silently merge two fields in a reader's head.
 *
 * The rows render whether or not a review exists, every absent value reading
 * `N/A` — a GOTU-7339 decision that keeps both cards the same shape either way.
 * The cost is that "the office never rated this professional" and "it rated
 * them but left the fields empty" now look identical; the absent star is the
 * only thing separating them.
 */
export const ProfessionalReviewCard = ({ professionalName, review }) => (
  <ReviewCard
    label="Professional review"
    name={review?.professionalName || professionalName}
    rating={review?.rating}
  >
    <Row gap={4} align="start">
      {/* Figma lays the three scores out as one row of stacked cells rather
          than three label/value lines. `divider={false}` because the rule
          below them is a single full-width one, not three cell-width ones. */}
      <Expanded className="min-w-0">
        <DataRow
          orientation="stacked"
          divider={false}
          label="Professional Skills"
          value={orNotAvailable(review?.professionalSkills)}
        />
      </Expanded>
      <Expanded className="min-w-0">
        <DataRow
          orientation="stacked"
          divider={false}
          label="Interpersonal Skills"
          value={orNotAvailable(review?.interpersonalSkills)}
        />
      </Expanded>
      <Expanded className="min-w-0">
        <DataRow
          orientation="stacked"
          divider={false}
          label="Time Management"
          value={orNotAvailable(review?.timeManagement)}
        />
      </Expanded>
    </Row>

    <Divider />

    <DataRow
      orientation="stacked"
      label="Confidential Feedback for GoTu"
      value={orNotAvailable(review?.confidentialFeedback)}
    />
  </ReviewCard>
);

ProfessionalReviewCard.propTypes = {
  /** Fallback heading, from the job, for when there is no review to name it. */
  professionalName: PropTypes.string,
  /** From `toProfessionalReviewCardModel`; null when the office never rated. */
  review: PropTypes.shape({
    professionalName: PropTypes.string,
    rating: PropTypes.number,
    professionalSkills: PropTypes.number,
    interpersonalSkills: PropTypes.number,
    timeManagement: PropTypes.number,
    confidentialFeedback: PropTypes.string,
  }),
};

ProfessionalReviewCard.defaultProps = {
  professionalName: '',
  review: null,
};

export default ProfessionalReviewCard;
